// Dashboard — ported from miluliem's layout where the data exists.
//
// Sections omitted vs. miluliem and the reason:
//   - COGS / Gross profit / Margin cards: products table has no cost_price.
//   - Website performance / Device breakdown / Funnel chart / Search summary /
//     Client errors / Top pages / Most viewed: Vīnoga storefront has no
//     visitor beacon yet (dashboard "B" in the chat thread). Adding fake
//     numbers would lie, so the sections aren't rendered.
//
// All other sections (financial KPIs, Operations, Top brands/categories/
// products, Couriers, Payment methods, Revenue by channel) hydrate from
// /api/v1/admin/dashboard in a single request.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Archive,
  Award,
  CreditCard,
  Layers,
  Package,
  ShoppingCart,
  Tag,
  Truck,
  TrendingUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

type RangePreset =
  | "today"
  | "yesterday"
  | "7d"
  | "30d"
  | "90d"
  | "this_month"
  | "this_year"
  | "all";

// Compute (from, to) ISO bounds for a preset. `to` is exclusive (matches
// the backend's `created_at < to` predicate), so e.g. "today" returns
// [start-of-today, start-of-tomorrow). Empty strings mean "open bound" —
// the backend interprets the [from='', to=''] case as all-time.
function presetToRange(preset: RangePreset): { from: string; to: string } {
  const now = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const today = startOfDay(now);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  switch (preset) {
    case "today":
      return { from: today.toISOString(), to: tomorrow.toISOString() };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: yesterday.toISOString(), to: today.toISOString() };
    }
    case "7d": {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { from: start.toISOString(), to: tomorrow.toISOString() };
    }
    case "30d": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { from: start.toISOString(), to: tomorrow.toISOString() };
    }
    case "90d": {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { from: start.toISOString(), to: tomorrow.toISOString() };
    }
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: start.toISOString(), to: tomorrow.toISOString() };
    }
    case "this_year": {
      const start = new Date(today.getFullYear(), 0, 1);
      return { from: start.toISOString(), to: tomorrow.toISOString() };
    }
    case "all":
      return { from: "", to: "" };
  }
}

type DashboardData = {
  range: {
    from: string;
    to: string;
    priorFrom: string;
    priorTo: string;
  };
  financials: {
    revenueCurrent: number;
    revenuePrior: number;
    revenueAllTime: number;
    ordersCurrent: number;
    ordersPrior: number;
    ordersAllTime: number;
    avgCart: number;
    vatCurrent: number;
    vatAllTime: number;
  };
  operations: {
    awaitingFulfilment: number;
    lowStock: number;
    outOfStock: number;
  };
  topProducts: Array<{ sku: string; name: string; qty: number; revenue: number }>;
  topBrands: Array<{ brand: string; qty: number; revenue: number }>;
  topCategories: Array<{ slug: string; name: string; qty: number; revenue: number }>;
  payments: Array<{ method: string; count: number; revenue: number }>;
  couriers: Array<{ provider: string; count: number }>;
  channels: Array<{ channel: string; count: number; revenue: number; avgValue: number }>;
};

function fmtEuro(n: number): string {
  return `€${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function deltaPct(current: number, prior: number): number | null {
  if (prior === 0) return current === 0 ? 0 : null;
  return ((current - prior) / prior) * 100;
}

export function Dashboard() {
  const t = useT();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<RangePreset>("7d");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const { from, to } = presetToRange(preset);
        const qs = new URLSearchParams();
        qs.set("from", from);
        qs.set("to", to);
        const res = await api<DashboardData>(
          `/api/v1/admin/dashboard?${qs.toString()}`
        );
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [preset]);

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        {t("dashboard.loadError", "Failed to load dashboard")}: {error}
      </div>
    );
  }

  const isLoading = !data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">
            {t("dashboard.title", "Dashboard")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("dashboard.subtitle", "Operational overview. Click any card to drill in.")}
          </p>
        </div>
        <Select value={preset} onValueChange={(v) => setPreset(v as RangePreset)}>
          <SelectTrigger className="h-9 w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">{t("dashboard.rangeToday", "Today")}</SelectItem>
            <SelectItem value="yesterday">{t("dashboard.rangeYesterday", "Yesterday")}</SelectItem>
            <SelectItem value="7d">{t("dashboard.range7d", "Last 7 days")}</SelectItem>
            <SelectItem value="30d">{t("dashboard.range30d", "Last 30 days")}</SelectItem>
            <SelectItem value="90d">{t("dashboard.range90d", "Last 90 days")}</SelectItem>
            <SelectItem value="this_month">{t("dashboard.rangeThisMonth", "This month")}</SelectItem>
            <SelectItem value="this_year">{t("dashboard.rangeThisYear", "This year")}</SelectItem>
            <SelectItem value="all">{t("dashboard.rangeAll", "All time")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ── Financial KPI cards ─────────────────────────────────────── */}
      <KpiRow data={data} t={t} loading={isLoading} />

      {/* ── Revenue by channel ──────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Layers size={16} className="text-muted-foreground" />
            {t("dashboard.revenueByChannel", "Revenue by channel")}
          </CardTitle>
          <CardDescription>
            {t(
              "dashboard.revenueByChannelDesc",
              "Where orders originate. New channels (POS, third-party) drop in as they ship."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.channel", "Channel")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.orders", "Orders")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.revenue", "Revenue")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.avgValue", "Avg value")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.channels.map((c) => (
                <TableRow key={c.channel}>
                  <TableCell className="capitalize">{c.channel}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtEuro(c.revenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtEuro(c.avgValue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Operations ──────────────────────────────────────────────── */}
      <div>
        <h2 className="mb-3 font-display text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {t("dashboard.operations", "Operations")}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <OpsCard
            title={t("dashboard.awaitingFulfilment", "Awaiting fulfilment")}
            value={isLoading ? "…" : data!.operations.awaitingFulfilment.toLocaleString()}
            icon={<Package size={18} className="text-amber-500" />}
            href="/orders"
          />
          <OpsCard
            title={t("dashboard.lowStock", "Low stock (<5)")}
            value={isLoading ? "…" : data!.operations.lowStock.toLocaleString()}
            icon={<AlertCircle size={18} className="text-orange-500" />}
            href="/inventory"
          />
          <OpsCard
            title={t("dashboard.outOfStock", "Out of stock")}
            value={isLoading ? "…" : data!.operations.outOfStock.toLocaleString()}
            icon={<Archive size={18} className="text-destructive" />}
            href="/inventory"
          />
        </div>
      </div>

      {/* ── Top brands & categories ─────────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-2">
        <RankingCard
          title={t("dashboard.topBrands", "Top brands")}
          icon={<Award size={16} className="text-muted-foreground" />}
          rows={data?.topBrands.map((b) => ({
            key: b.brand,
            label: b.brand,
            qty: b.qty,
            revenue: b.revenue,
          })) ?? []}
          loading={isLoading}
          t={t}
        />
        <RankingCard
          title={t("dashboard.topCategories", "Top categories")}
          icon={<Tag size={16} className="text-muted-foreground" />}
          rows={data?.topCategories.map((c) => ({
            key: c.slug,
            label: c.name,
            qty: c.qty,
            revenue: c.revenue,
            href: `/category/${c.slug}`,
          })) ?? []}
          loading={isLoading}
          t={t}
        />
      </div>

      {/* ── Top products ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <TrendingUp size={16} className="text-muted-foreground" />
            {t("dashboard.topProducts", "Top products")}
          </CardTitle>
          <CardDescription>
            {t("dashboard.byRevenueRange", "By revenue, selected range")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[100px] text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.sku", "SKU")}
                </TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.product", "Product")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.qty", "Qty")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.revenue", "Revenue")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.topProducts.map((p) => (
                <TableRow key={p.sku}>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {p.sku}
                  </TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {p.qty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtEuro(p.revenue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Payment methods + Couriers ──────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <CreditCard size={16} className="text-muted-foreground" />
              {t("dashboard.paymentMethods", "Payment methods")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                    {t("dashboard.method", "Method")}
                  </TableHead>
                  <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                    {t("dashboard.orders", "Orders")}
                  </TableHead>
                  <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                    {t("dashboard.revenue", "Revenue")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.payments.map((p) => (
                  <TableRow key={p.method}>
                    <TableCell className="capitalize">
                      {p.method.replace(/_/g, " ")}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {p.count.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtEuro(p.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Truck size={16} className="text-muted-foreground" />
              {t("dashboard.couriers", "Couriers")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data && data.couriers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("dashboard.couriersEmpty", "No courier shipments yet.")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                      {t("dashboard.provider", "Provider")}
                    </TableHead>
                    <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                      {t("dashboard.shipments", "Shipments")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.couriers.map((c) => (
                    <TableRow key={c.provider}>
                      <TableCell className="capitalize">{c.provider}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.count.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiRow({
  data,
  t,
  loading,
}: {
  data: DashboardData | null;
  t: (k: string, fallback: string) => string;
  loading: boolean;
}) {
  const f = data?.financials;
  const cards: Array<{
    title: string;
    value: string;
    delta: string | null;
    description: string;
    href: string;
    icon: React.ReactNode;
    accent?: boolean;
  }> = [
    {
      title: t("dashboard.orders", "Orders"),
      value: loading ? "…" : (f!.ordersCurrent).toLocaleString(),
      delta:
        loading || !f
          ? null
          : fmtDelta(deltaPct(f.ordersCurrent, f.ordersPrior)),
      description: t(
        "dashboard.allTimeOrders",
        `${loading ? "…" : f!.ordersAllTime.toLocaleString()} all-time`
      ),
      href: "/orders",
      icon: <ShoppingCart size={18} className="text-primary" />,
      accent: true,
    },
    {
      title: t("dashboard.revenue", "Revenue"),
      value: loading ? "…" : fmtEuro(f!.revenueCurrent),
      delta:
        loading || !f
          ? null
          : fmtDelta(deltaPct(f.revenueCurrent, f.revenuePrior)),
      description: t(
        "dashboard.allTimeRevenue",
        `${loading ? "…" : fmtEuro(f!.revenueAllTime)} all-time`
      ),
      href: "/orders",
      icon: <TrendingUp size={18} className="text-emerald-500" />,
    },
    {
      title: t("dashboard.vat", "VAT"),
      value: loading ? "…" : fmtEuro(f!.vatCurrent),
      delta: null,
      description: t(
        "dashboard.vatDesc",
        `${loading ? "…" : fmtEuro(f!.vatAllTime)} all-time · 21% inc.`
      ),
      href: "/orders",
      icon: <Layers size={18} className="text-blue-500" />,
    },
    {
      title: t("dashboard.avgCart", "Average cart"),
      value: loading ? "…" : fmtEuro(f!.avgCart),
      delta: null,
      description: t(
        "dashboard.avgCartDesc",
        "Across orders in this range"
      ),
      href: "/orders",
      icon: <Tag size={18} className="text-purple-500" />,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <Link key={card.title} to={card.href} className="group">
          <Card
            className={
              "transition-colors group-hover:border-foreground/40 " +
              (card.accent ? "border-primary/40" : "")
            }
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.title}
              </CardTitle>
              {card.icon}
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tabular-nums text-foreground">
                {card.value}
              </div>
              <div className="mt-1 flex items-center justify-between">
                <CardDescription className="text-xs">
                  {card.description}
                </CardDescription>
                {card.delta && (
                  <span
                    className={
                      "text-xs font-medium tabular-nums " +
                      (card.delta.startsWith("+")
                        ? "text-emerald-500"
                        : card.delta.startsWith("-")
                          ? "text-destructive"
                          : "text-muted-foreground")
                    }
                  >
                    {card.delta}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

function fmtDelta(d: number | null): string | null {
  if (d === null) return null;
  return fmtPct(d);
}

function OpsCard({
  title,
  value,
  icon,
  href,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  href: string;
}) {
  return (
    <Link to={href} className="group">
      <Card className="transition-colors group-hover:border-foreground/40">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {title}
          </CardTitle>
          {icon}
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-semibold tabular-nums text-foreground">
            {value}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function RankingCard({
  title,
  icon,
  rows,
  loading,
  t,
}: {
  title: string;
  icon: React.ReactNode;
  rows: Array<{
    key: string;
    label: string;
    qty: number;
    revenue: number;
    href?: string;
  }>;
  loading: boolean;
  t: (k: string, fallback: string) => string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          {title}
        </CardTitle>
        <CardDescription>
          {t("dashboard.byRevenueRange", "By revenue, selected range")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("dashboard.noData", "No data yet.")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[11px] font-bold uppercase tracking-wider">
                  {title}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.qty", "Qty")}
                </TableHead>
                <TableHead className="text-right text-[11px] font-bold uppercase tracking-wider">
                  {t("dashboard.revenue", "Revenue")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="font-medium">
                    {r.href ? (
                      <Link
                        to={r.href}
                        className="text-foreground hover:text-primary"
                      >
                        {r.label}
                      </Link>
                    ) : (
                      r.label
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.qty.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtEuro(r.revenue)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
